import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

// Editorial-toned button. Variants are deliberate, not exhaustive — we add
// more only when a real surface needs them. Per anti-template policy:
// each surface that uses Button must answer the "believable in a real
// product screenshot?" question in PR review.

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2',
    'rounded-[var(--radius-md)]',
    'font-medium',
    'transition-[background,color,box-shadow]',
    'duration-[var(--duration-fast)]',
    'ease-[var(--ease-out-quart)]',
    'disabled:cursor-not-allowed disabled:opacity-60',
    'focus-visible:outline-none',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-[var(--color-accent)] text-[var(--color-ink-on-accent)]',
          'hover:bg-[var(--color-accent-hover)]',
          'shadow-[var(--shadow-sm)]',
        ],
        secondary: [
          'bg-[var(--color-surface-elevated)] text-[var(--color-ink)]',
          'border border-[var(--color-border)]',
          'hover:bg-[var(--color-surface-sunken)]',
        ],
        ghost: ['bg-transparent text-[var(--color-ink)]', 'hover:bg-[var(--color-surface-sunken)]'],
        danger: ['bg-[var(--color-danger)] text-[var(--color-ink-on-accent)]', 'hover:opacity-90'],
      },
      size: {
        sm: 'h-9 px-3 text-[var(--text-sm)]',
        md: 'h-11 px-5 text-[var(--text-base)]',
        lg: 'h-12 px-6 text-[var(--text-base)]',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...rest}
    />
  );
});
